interface ActivityEvent {
  id: string;
  type: 'quotation' | 'customer' | 'company' | 'import' | 'system';
  title: string;
  detail: string;
  timestamp: string;
  amount?: number;
}

interface ActivityTimelineProps {
  events: ActivityEvent[];
}

const TYPE_CONFIG = {
  quotation: { icon: '📄', color: 'bg-blue-500', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
  customer: { icon: '👥', color: 'bg-emerald-500', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' },
  company: { icon: '🏢', color: 'bg-purple-500', bgColor: 'bg-purple-50', textColor: 'text-purple-700' },
  import: { icon: '📥', color: 'bg-amber-500', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
  system: { icon: '⚙️', color: 'bg-slate-500', bgColor: 'bg-slate-100', textColor: 'text-slate-700' },
};

function timeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return then.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function ActivityTimeline({ events }: ActivityTimelineProps) {
  if (events.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-3xl mb-2">📋</div>
        <p className="text-xs text-slate-400">No recent activity</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {events.map((event, i) => {
        const config = TYPE_CONFIG[event.type];
        const isLast = i === events.length - 1;
        return (
          <div key={event.id} className="flex gap-3 group">
            {/* Timeline line + dot */}
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center text-sm flex-shrink-0 border-2 border-white shadow-sm group-hover:scale-110 transition-transform`}>
                {config.icon}
              </div>
              {!isLast && <div className="w-0.5 h-full bg-slate-200 my-1" />}
            </div>
            {/* Content */}
            <div className={`flex-1 ${!isLast ? 'pb-4' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-bold text-slate-900 group-hover:text-blue-700 transition-colors">
                    {event.title}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{event.detail}</p>
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <span className="text-[9px] font-bold text-slate-400">{timeAgo(event.timestamp)}</span>
                  {event.amount !== undefined && (
                    <span className={`text-[10px] font-black ${config.textColor} mt-0.5`}>
                      ₹{event.amount.toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
